import { RegexExpression } from "./type";

// Data attributes for inputs
const DATA_LM_REQUIRED = "data-lm-required";
const DATA_LM_MAX = "data-lm-max";
const DATA_LM_MIN = "data-lm-min";
const DATA_LM_COUNTRY = "data-lm-country";
const DATA_LM_COMPANY_EMAIL = "data-lm-company-email";

// Regex for form validation
const regexExpression: RegexExpression = {
  in: {
    name: /^[a-zA-Z ]{3,30}$/,
    email: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
    mobile: /^(\+91[\-\s]?)?[0]?(91)?[6789]\d{9}$/,
    whatsapp: /^(\+91[\-\s]?)?[0]?(91)?[6789]\d{9}$/,
  },
  us: {
    name: /^[\p{L} .'-]{3,40}$/u,
    email: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
    mobile: /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/,
    whatsapp: /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/,
  },
};

// Get the API key from the meta tag
const apiKeyMetaTag = document.querySelector(
  'meta[name="api-key"]',
) as HTMLMetaElement;

const baseUrlMetaTag = document.querySelector(
  'meta[name="base-url"]',
) as HTMLMetaElement;

// Custom lead source configured per-landing-page in the builder settings.
// Used as a fallback utm_source when the visit carries no utm_source param
// (e.g. direct/organic traffic with no ad platform to attribute to).
const customSourceMetaTag = document.querySelector(
  'meta[name="custom-source"]',
) as HTMLMetaElement;

const apiKey = apiKeyMetaTag ? apiKeyMetaTag.content : "";
const baseUrl = baseUrlMetaTag ? baseUrlMetaTag.content : "";
const customSource = customSourceMetaTag
  ? customSourceMetaTag.content || "Landing Page"
  : "Landing Page";
const formUrl =
  baseUrl || "https://client-api3.ind.leadmetrics.ai/v1/leads"; // Updated to point to new API without formId

// Anti-spam measures
const MIN_FORM_FILL_TIME = 3000; // Minimum 3 seconds to fill form (prevents bots)
let formStartTime: number = 0;

// Rate limiting — prevent repeated rapid submissions per form
const RATE_LIMIT_WINDOW = 5 * 60 * 1000; // 5-minute window
const MAX_SUBMISSIONS_PER_WINDOW = 3;

// Human interaction signals — real users always generate at least one of these
let hasMouseMoved = false;
let hasHumanInput = false;

document.addEventListener(
  "mousemove",
  () => {
    hasMouseMoved = true;
  },
  { once: true, passive: true },
);
document.addEventListener(
  "touchstart",
  () => {
    hasMouseMoved = true;
  },
  { once: true, passive: true },
);
document.addEventListener(
  "keydown",
  () => {
    hasHumanInput = true;
  },
  { once: true, passive: true },
);

// Function to check if form was filled too quickly (bot detection)
function isFilledTooQuickly(): boolean {
  return formStartTime > 0 && Date.now() - formStartTime < MIN_FORM_FILL_TIME;
}

// Rate limiting via localStorage
function isRateLimited(): boolean {
  try {
    const key = `_lm_rl_global`;
    const stored = localStorage.getItem(key);
    if (!stored) return false;
    const data = JSON.parse(stored) as { t: number[] };
    const now = Date.now();
    const recent = data.t.filter((ts) => now - ts < RATE_LIMIT_WINDOW);
    return recent.length >= MAX_SUBMISSIONS_PER_WINDOW;
  } catch {
    return false;
  }
}

function recordSubmission(): void {
  try {
    const key = `_lm_rl_global`;
    const now = Date.now();
    const stored = localStorage.getItem(key);
    let timestamps: number[] = [];
    if (stored) {
      const data = JSON.parse(stored) as { t: number[] };
      timestamps = data.t.filter((ts) => now - ts < RATE_LIMIT_WINDOW);
    }
    timestamps.push(now);
    localStorage.setItem(key, JSON.stringify({ t: timestamps }));
  } catch {
    // localStorage unavailable — skip silently
  }
}

// Reject submissions with no recorded human-interaction events
function lacksHumanInteraction(): boolean {
  return !hasMouseMoved && !hasHumanInput;
}

// Function to detect script injection attempts
function containsScriptInjection(value: string): boolean {
  const scriptPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi, // onload, onclick, etc.
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /<link/gi,
    /<meta/gi,
    /expression\s*\(/gi, // CSS expression
    /vbscript:/gi,
    /data:text\/html/gi,
    /eval\s*\(/gi,
    /setTimeout\s*\(/gi,
    /setInterval\s*\(/gi,
    /Function\s*\(/gi,
  ];

  return scriptPatterns.some((pattern) => pattern.test(value));
}

// Function to sanitize input by removing potentially dangerous characters
function sanitizeInput(value: string): string {
  return value
    .replace(/[<>]/g, "") // Remove angle brackets
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, "") // Remove event handlers
    .trim();
}

function validateForm(
  input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  form: HTMLFormElement,
): boolean {
  const name = input.name;
  const value = input.value;

  const dataLmRequired = input.getAttribute(DATA_LM_REQUIRED);
  const maxAttribute = input.getAttribute(DATA_LM_MAX);
  const minAttribute = input.getAttribute(DATA_LM_MIN);
  const countryAttribute = input.getAttribute(DATA_LM_COUNTRY);
  const companyEmailAttribute = input.getAttribute(DATA_LM_COMPANY_EMAIL);

  const errorElement = form.querySelector(`#${name}Error`);

  if (errorElement) {
    // Check for script injection attempts
    if (containsScriptInjection(value)) {
      errorElement.textContent =
        "Invalid characters detected. Please remove any script tags or special characters.";
      return false;
    }

    // Check if error element;
    if (dataLmRequired === "true") {
      // required
      if (value === "") {
        errorElement.textContent = `Please enter your ${name}`;
        return false;
      }
    }

    // Name validation using regex
    if (name === "name") {
      const country = countryAttribute === "us" ? "us" : "in";
      if (!regexExpression[country].name.test(value)) {
        errorElement.textContent = "Please enter a valid name";
        return false;
      }
    }

    if (minAttribute) {
      // min length

      const minLength = parseInt(minAttribute);

      if (value.length < minLength) {
        errorElement.textContent = `Must have minimum ${minLength} characters`;
        return false;
      }
    }

    if (maxAttribute) {
      // max length
      const maxLength = parseInt(maxAttribute);
      if (value.length > maxLength) {
        errorElement.textContent = `Can only have maximum ${maxLength} characters!`;
        return false;
      }
    }

    if (countryAttribute === "us") {
      // us country code for numbers

      const key = name as keyof RegexExpression["us"];

      if (!regexExpression.us[key].test(value)) {
        errorElement.textContent = "Please enter a valid mobile number";
        return false;
      }
    }

    if (countryAttribute === "in") {
      // in country code for numbers

      const key = name as keyof RegexExpression["in"];

      if (!regexExpression.in[key].test(value)) {
        errorElement.textContent = "Please enter a valid mobile number";
        return false;
      }
    }

    if (companyEmailAttribute === "true") {
      const publicDomains = [
        "gmail.com",
        "yahoo.com",
        "hotmail.com",
        "aol.com",
        "outlook.com",
        "protonmail.com",
        "icloud.com",
        "zoho.com",
        "gmx.com",
        "mail.com",
        "yandex.com",
      ];

      const email = value.trim();
      const emailDomain = email.split("@")[1]?.toLowerCase();

      if (!regexExpression.in.email.test(email)) {
        errorElement.textContent = "Please enter a valid email";
        return false;
      }

      if (emailDomain && publicDomains.includes(emailDomain)) {
        errorElement.textContent =
          "Please use a company email address (not a public domain).";
        return false;
      }
    }
    return true;
  }

  return false;
}

function getFormData(form: HTMLFormElement) {
  const formData: Record<string, string> = {};

  form.querySelectorAll("input, textarea, select").forEach((element) => {
    const input = element as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement;
    const key = input.name;

    if (key) {
      // Handle checkboxes and radio buttons
      if (
        input instanceof HTMLInputElement &&
        (input.type === "checkbox" || input.type === "radio")
      ) {
        if (input.checked) {
          formData[key] = input.value || "on";
        }
      } else {
        formData[key] = input.value;
      }
    }
  });
  return formData;
}

const getUTMTags = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const utmTags: Record<string, string> = {};

  [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ].forEach((utmKey) => {
    if (params.has(utmKey)) {
      utmTags[utmKey] = params.get(utmKey) || "";
    }
  });

  // No utm_source on this visit (e.g. direct/organic traffic with no ad
  // platform to attribute to) — fall back to the page's configured custom
  // source so the lead still carries a meaningful source value.
  if (!utmTags.utm_source) {
    utmTags.utm_source = customSource;
  }

  return utmTags;
};

// Setup form submission with validation
function setupFormSubmission(
  form: HTMLFormElement,
  apiUrl: string,
  appendToMessage: boolean,
): void {
  const redirectUrl = form.getAttribute("data-lm-form-redirect") || "/";

  // Guard against double-submission (e.g. double-click or rapid re-submit)
  let isSubmitting = false;

  // Declare formData outside the event listener to keep it in sync
  let formData = getFormData(form);

  form.addEventListener(
    "submit",
    async function (event: SubmitEvent): Promise<void> {
      event.preventDefault();

      // Prevent double-submission
      if (isSubmitting) return;

      let isValid = true; // Start with the assumption that the form is valid
      const errorElements = form.querySelectorAll(".error");

      errorElements.forEach((errorElement) => {
        errorElement.textContent = ""; // Clear previous error messages
      });

      // Re-fetch form data on submission
      formData = getFormData(form);

      // Perform validation for all fields, even if they are empty
      form.querySelectorAll("input, select, textarea").forEach((field) => {
        const input = field as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement;
        isValid = validateForm(input, form) && isValid;
      });

      // Anti-spam checks before submission
      if (isValid) {
        // Check if form was filled too quickly (bot detection)
        if (isFilledTooQuickly()) {
          isValid = false;
        }

        // Human interaction — reject if no mouse/touch/keyboard events were ever recorded
        if (lacksHumanInteraction()) {
          isValid = false;
        }

        // Rate limiting — prevent more than MAX_SUBMISSIONS_PER_WINDOW submissions in a window
        if (isRateLimited()) {
          const firstError = form.querySelector(".error");
          if (firstError) {
            firstError.textContent =
              "Too many submissions. Please wait a few minutes before trying again.";
          }
          isValid = false;
        }
      }

      // If form is valid, proceed with submission
      if (isValid) {
        const submitBtn = form.querySelector("#submitBtn") as HTMLButtonElement;
        const originalButtonText = submitBtn.textContent;

        submitBtn.disabled = true;
        submitBtn.textContent = "Submitting...";
        isSubmitting = true;

        // Get UTM tags from the URL
        const utmTags = getUTMTags();

        // Prepare optional appended message block
        if (appendToMessage) {
          const baseFields = [
            "name",
            "email",
            "company",
            "mobile",
            "whatsapp",
            "message",
          ];
          const additionalFields = Object.keys(formData)
            .filter((key) => {
              const value = formData[key];
              return !baseFields.includes(key) && value && value.trim() !== "";
            })
            .map((key) => ({ key, value: formData[key] }));

          if (additionalFields.length > 0) {
            const extraText = additionalFields
              .map(({ key, value }) => `${key}: ${value}`)
              .join(", ");

            formData.message = formData.message
              ? `${formData.message}, ${extraText}`
              : extraText;
          }
        }

        const payload = {
          ...formData,
          ...utmTags,
        };

        try {
          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Api-Key": apiKey, // Use the API key from the meta tag
              "X-Requested-With": "XMLHttpRequest", // CSRF protection
              Accept: "application/json",
              "Cache-Control": "no-cache", // Prevent caching of sensitive requests
            },
            body: JSON.stringify(payload),
          });
          const resData = await response.json();

          if (resData) {
            recordSubmission();
            showModal(
              "Thank you!",
              "We will contact you soon...",
              redirectUrl,
              "Go to Website",
            );
            form.reset();
          }
        } catch (error) {
          console.error(error);
          showModal("Submission Failed!", "Please try again", "/", "Close");
        } finally {
          submitBtn.disabled = false;
          submitBtn.textContent = originalButtonText;
          isSubmitting = false;
        }
      }
    },
  );

  // Track when user starts interacting with the form (anti-bot measure)
  let hasStartedInteracting = false;

  // Add blur event listeners for validation and input handling
  form.querySelectorAll("input, textarea, select").forEach((field) => {
    const input = field as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement;
    const key = input.name;

    // Track form start time on first interaction
    field.addEventListener("focus", function () {
      if (!hasStartedInteracting) {
        formStartTime = Date.now();
        hasStartedInteracting = true;
      }
    });

    field.addEventListener("blur", function () {
      // Sanitize input on blur
      const sanitizedValue = sanitizeInput(input.value);
      input.value = sanitizedValue;
      formData[key] = sanitizedValue;

      // Perform validation on blur for the specific field
      validateForm(input, form);
    });

    // Clear error message and update formData on input
    field.addEventListener("input", function () {
      formData[key] = input.value; // Update formData
      const errorElement = form.querySelector(`#${input.name}Error`);
      if (errorElement) {
        errorElement.textContent = ""; // Clear error for this field
      }
    });
  });
}

// Initialize form submissions for all forms with data-lm-form attribute
function initializeForm(appendToMessage = false) {
  document.querySelectorAll("form[data-lm-form]").forEach((form) => {
    const formElement = form as HTMLFormElement;
    setupFormSubmission(formElement, formUrl, appendToMessage);
  });
}

// Show modal function
function showModal(
  title: string,
  message: string,
  link: string,
  buttonText: string,
): void {
  const customModal = document.getElementById("customModal") as HTMLElement;
  const modalTitle = document.getElementById("modalTitle") as HTMLElement;
  const modalMessage = document.getElementById("modalMessage") as HTMLElement;
  const modalButton = document.getElementById("modalButton");
  const closeButton = document.getElementById("closeButton");

  customModal.classList.add("flex");
  customModal.classList.remove("hidden");
  modalTitle.textContent = title;
  modalMessage.textContent = message;
  if (modalButton) {
    modalButton.textContent = buttonText;
    modalButton.removeEventListener("click", modalButtonClickHandler);
    modalButton.addEventListener("click", modalButtonClickHandler);
  }
  if (closeButton) {
    closeButton.removeEventListener("click", modalCloseHandler);
    closeButton.addEventListener("click", modalCloseHandler);
  }

  function modalCloseHandler() {
    customModal.classList.add("hidden");
    customModal.classList.remove("flex");
  }

  function modalButtonClickHandler() {
    window.location.href = link;
  }
}

export const formService = {
  initializeForm,
};
